<template>
    <v-row justify="end" class="ma-0 pa-0">
        <v-dialog
                v-model="dialog"
                max-width="800px"
                class=""
                hide-overlay
                width="unset"
                :fullscreen="$vuetify.breakpoint.xsOnly"
                transition="dialog-transition"
        >
            <v-card style="min-width: 450px" class="card">
                <v-container class="container pa-7">
                    <template>
                        <v-row class="justify-end">
                            <div class="headSection">
                                <div class="right-aligned-child" @click="close">X</div>
                            </div>
                        </v-row>
                        <v-row>
                            <div class="d-flex align-center">
                                <div class="w-100 text-h5">
                                    Report - Settings
                                </div>
                                <div class="mt-6">
                                    <v-combobox
                                            v-model="selectedSetting"
                                            :items="profileSettings"
                                            item-text="name"
                                            item-value="id"
                                            placeholder="-- Load or Save Settings --"
                                            outlined
                                            hide-no-data
                                            class="custom-combobox"
                                            @change="handleSelection"
                                            @blur="handleBlur"
                                            @input="handleInput"
                                            rounded
                                    >
                                    </v-combobox>
                                </div>
                                <div class="ml-5">
                                    <v-btn color="primary"
                                           :disabled="isDefaultProfile"
                                           v-if="isDataChanged()"
                                           @click="saveSettings"
                                    >
                                        <v-icon class="mr-2">mdi-content-save</v-icon>
                                        Save
                                    </v-btn>
                                </div>

                            </div>
                        </v-row>
                        <!-- Section Date Range -->
                        <v-row class="mt-10">
                            <div>
                                <div class="d-flex align-center">
                                    <v-icon
                                        @click="isDateRangeSettingsVisible = !isDateRangeSettingsVisible"
                                        class="mr-2"
                                        :class="{ 'rotate-icon': isDateRangeSettingsVisible }"
                                    >
                                        mdi-chevron-down
                                    </v-icon>
                                    <h3 class="mb-0">Date Range</h3>
                                </div>
                                <v-expand-transition>
                                    <div v-show="isDateRangeSettingsVisible" class="mt-4 ml-10 w-75">
                                        <v-row class="mt-10">
                                            <DateRangePicker
                                                :key="firstMenuValue + secondMenuValue + selectedDateOne + selectedDateTwo"
                                                :range-first-menu-items="rangeFirstMenuItems"
                                                :current-items="currentItems"
                                                :previous-items="previousItems"
                                                :last-items="lastItems"
                                                :initial-first-menu-value="firstMenuValue"
                                                :initial-second-menu-value="secondMenuValue"
                                                :initial-last-value="lastValue"
                                                :initial-selected-date-one="selectedDateOne"
                                                :initial-selected-date-two="selectedDateTwo"
                                                @update="updateMainRange"
                                            />
                                        </v-row>
                                        <v-row class="mt-10">
                                            <v-switch v-model="compareTo" label="Compare to"></v-switch>
                                        </v-row>
                                        <v-row class="mt-10">
                                            <DateRangePicker
                                                v-if="compareTo"
                                                :key="compareFirstMenuValue + compareSecondMenuValue + compareSelectedDateOne + compareSelectedDateTwo"
                                                :range-first-menu-items="rangeFirstMenuItems"
                                                :current-items="currentItems"
                                                :previous-items="previousItems"
                                                :last-items="lastItems"
                                                :initial-first-menu-value="compareFirstMenuValue"
                                                :initial-second-menu-value="compareSecondMenuValue"
                                                :initial-last-value="compareLastValue"
                                                :initial-selected-date-one="compareSelectedDateOne"
                                                :initial-selected-date-two="compareSelectedDateTwo"
                                                @update="updateCompareRange"
                                            />
                                        </v-row>
                                    </div>
                                </v-expand-transition>
                            </div>
                        </v-row>
                        <!-- Section DrillDown -->
                        <v-row class="mt-10">
                            <div>
                                <div class="d-flex align-center">
                                    <v-icon
                                        @click="isDrillDownSettingsVisible = !isDrillDownSettingsVisible"
                                        class="mr-2"
                                        :class="{ 'rotate-icon': isDrillDownSettingsVisible }"
                                    >
                                        mdi-chevron-down
                                    </v-icon>
                                    <h3 class="mb-0">Data DrillDowns</h3>
                                </div>
                                <v-expand-transition>
                                    <div v-show="isDrillDownSettingsVisible" class="mt-4">
                                        <div v-for="(item, index) in selectedDrillDowns" :key="index" class="drilldown-item d-flex align-center">
                                            <v-row>
                                                <v-col cols="8" sm="6">
                                                    <v-select
                                                            :items="filteredDrillDowns(index)"
                                                            v-model="selectedDrillDowns[index]"
                                                            label=""
                                                            outlined
                                                            dense
                                                            class="tabs-select"
                                                    />
                                                </v-col>
                                                <v-col cols="1" sm="1">
                                                    <v-btn
                                                            icon
                                                            color="primary"
                                                            @click="removeDrillDown(index)"
                                                            :disabled="selectedDrillDowns.length === 1"
                                                    >
                                                        <v-icon size="34">mdi-delete-forever</v-icon>
                                                    </v-btn>
                                                </v-col>
                                            </v-row>
                                        </div>
                                        <v-btn color="primary" class="mt-3" @click="addDrillDown">
                                            <v-icon left>mdi-plus</v-icon>
                                            Add Drilldown
                                        </v-btn>
                                    </div>
                                </v-expand-transition>
                            </div>
                        </v-row>
                        <!-- Section Columns -->
                        <v-row class="mt-10">
                            <div>
                                <div class="d-flex align-center">
                                    <v-icon
                                            @click="isColumnsSettingsVisible = !isColumnsSettingsVisible"
                                            class="mr-2"
                                            :class="{ 'rotate-icon': isColumnsSettingsVisible }"
                                    >
                                        mdi-chevron-down
                                    </v-icon>
                                    <h3 class="mb-0">Columns Selection</h3>
                                </div>
                                <v-expand-transition>
                                    <div v-show="isColumnsSettingsVisible" class="mt-4">
                                        <v-row
                                                v-for="(item, index) in sortableItems"
                                                :key="item.name"
                                                class="drilldown-item align-center"
                                                draggable="true"
                                                @dragstart="onDragStart(index, $event)"
                                                @dragover.prevent
                                                @drop="onDrop(index, $event)"
                                        >
                                            <v-col cols="1" class="mb-5 drag-handle" style="cursor: grab;">
                                                <v-icon size="28">mdi-drag-horizontal-variant</v-icon>
                                            </v-col>

                                            <v-col cols="6">
                                                <v-select
                                                    class="tabs-select"
                                                    :class="{ 'disabled-select': item.isDrillDown }"
                                                    :items="item.isDrillDown ? [item.name] :  [...avaliableColumns, item.name]"
                                                    v-model="sortableItems[index].name"
                                                    :value="item.name"
                                                    @change="val => handleNameChange(val, index)"
                                                    :disabled="item.isDrillDown"
                                                    outlined
                                                    dense
                                                />
                                            </v-col>

                                            <v-col cols="3" style="margin-bottom: 3.5%">
                                                <v-select
                                                        :items="['asc', 'desc', '']"
                                                        :value="getSortingOrder(item.name)"
                                                        label="Sort"
                                                        outlined
                                                        dense
                                                        hide-details
                                                        @change="(val) => handleSortingChange(val, item.name)"
                                                        :class="{'active-sort': isSortedField(item.name)}"
                                                />
                                            </v-col>

                                            <v-col cols="2">
                                                <v-btn
                                                        v-if="!item.isDrillDown"
                                                        icon
                                                        color="primary"
                                                        @click="removeColumn(index)"
                                                >
                                                    <v-icon size="34" class="mb-6">mdi-delete-forever</v-icon>
                                                </v-btn>
                                            </v-col>
                                        </v-row>
                                        <v-btn color="primary" class="mt-3" @click="addColumn">
                                            <v-icon left>mdi-plus</v-icon>
                                            Add Columns
                                        </v-btn>
                                    </div>
                                </v-expand-transition>

                            </div>
                        </v-row>
                        <!-- Section Data Filters -->
                         <v-row class="mt-10">
                        <div>
                          <div class="d-flex align-center">
                            <v-icon
                                @click="isDataFiltersVisible = !isDataFiltersVisible"
                                class="mr-2"
                                :class="{ 'rotate-icon': isDataFiltersVisible }"
                            >
                              mdi-chevron-down
                            </v-icon>
                            <h3 class="mb-0">Data Filters</h3>
                          </div>
                          <v-expand-transition>
                            <div v-show="isDataFiltersVisible" class="mt-4 ml-3">
                              <div
                                  class="d-flex align-center mb-4"
                                  v-for="(filter, index) in selectedFilters"
                                  :key="filter.id"
                              >
                                <v-select
                                    class="mr-2"
                                    v-model="filter.column"
                                    :items="allFilters"
                                    solo
                                    dense
                                    hide-details
                                    rounded
                                    @change="updateField(filter)"
                                ></v-select>

                                <v-select
                                    class="mr-2"
                                    v-model="filter.operator"
                                    :items="availableOperators"
                                    solo
                                    dense
                                    hide-details
                                    rounded
                                ></v-select>

                                <v-text-field
                                    class="mr-2"
                                    v-model="filter.value"
                                    solo
                                    dense
                                    hide-details
                                    rounded
                                ></v-text-field>

                                  <v-btn
                                      icon
                                      color="primary"
                                      class="align-self-end mt-1"
                                      @click="removeFilter(filter.id)"
                                  >
                                      <v-icon size="34">mdi-delete-forever</v-icon>
                                  </v-btn>
                              </div>

                                <v-btn color="primary" class="mt-3" @click="addFilter">
                                    <v-icon left>mdi-plus</v-icon>
                                    Add Filter
                                </v-btn>
                            </div>
                          </v-expand-transition>
                        </div>
                      </v-row>

                        <!-- Section Misc -->
                        <v-row class="mt-10">
                            <div>
                                <div class="d-flex align-center">
                                    <v-icon
                                            @click="isMiscSettingsVisible = !isMiscSettingsVisible"
                                            class="mr-2"
                                            :class="{ 'rotate-icon': isMiscSettingsVisible }"
                                    >
                                        mdi-chevron-down
                                    </v-icon>
                                    <h3 class="mb-0">Misc</h3>
                                </div>
                                <v-expand-transition>
                                    <div v-show="isMiscSettingsVisible" class="mt-4 ml-3">
                                        <div v-if="user[0].can('see_blacklisted')" class="d-sm-inline-block">
                                            <v-switch class="d-sm-inline-block" v-model="isShowBlacklisted"></v-switch>
                                            <span class="inactive_offer_text px-4" >Show Blacklisted Affiliated</span>
                                        </div>
                                        <div class="mt-5">
                                            <span class="d-sm-inline-block label-misc inactive_offer_text mr-2">Offer Mode</span>
                                            <v-select
                                                    class="d-sm-inline-block w-50"
                                                    v-if="user[0].can('reports_both_POV')"
                                                    v-model="onlyAggregatedStats"
                                                    :items="[
                                                        { text: 'Affiliate (Entry)', value: true },
                                                        { text: 'Advertiser (Exit)', value: false }
                                                    ]"
                                                    solo
                                                    dense
                                                    hide-details
                                                    rounded
                                            ></v-select>
                                        </div>
                                        <div class="mt-5">
                                            <span class="d-sm-inline-block label-misc inactive_offer_text mr-2">Density</span>
                                            <v-select
                                                    class="d-sm-inline-block w-50"
                                                    v-if="user[0].can('reports_both_POV')"
                                                    v-model="mode"
                                                    :items="[
                                                        { text: 'Comfortable', value: 'comfortable' },
                                                        { text: 'Compact', value: 'compact' }
                                                ]   "
                                                    solo
                                                    dense
                                                    hide-details
                                                    rounded
                                            ></v-select>
                                        </div>
                                    </div>
                                </v-expand-transition>
                            </div>
                        </v-row>
                        <!-- Action -->
                        <v-row class="mt-10 justify-space-between">
                            <v-btn color="primary" @click="resetSettings">Reset</v-btn>
                            <v-btn color="primary" @click="applySettings">Apply</v-btn>
                        </v-row>
                    </template>
                </v-container>
            </v-card>
        </v-dialog>
    </v-row>
</template>

<script>
    import Vue from "vue";
    import { mapGetters, mapState, mapActions } from 'vuex'
    import store from "@/store";
    import {
        NAVIGATION_ITEMS,
        DRILL_DOWNS,
        COLUMNS,
        ALL_FILTERS,
        AVAILABLE_OPERATORS
    } from "@/constants";
    import dateRange from "@/helpers/dateRange";
    import DateRangePicker from "@/components/common/DateRangePicker";

    export default {
        components: {
          DateRangePicker
        },
        data: () => ({
            originData: null,
            dialog: false,
            isShowBlacklisted: false,
            onlyAggregatedStats: true,
            mode: 'comfortable',
            profileSettings: [],
            selectedSetting: null,
            selectedSettingId: 0,
            newSettingName: "",

            isDataFiltersVisible: false,
            isTabsSettingsVisible: false,
            isDrillDownSettingsVisible: false,
            isColumnsSettingsVisible: false,
            isDateRangeSettingsVisible: false,
            isMiscSettingsVisible: false,

            allTabs: [],
            selectedTabs: [],

            sorting: [],
            currentSortingField: null,

            allDrillDowns: [],
            selectedDrillDowns: [],
            previousDrillDowns: [],
            allColumns: [],
            selectedColumns: [],
            avaliableColumns: [],

            wasManuallySorted: false,

            sortableItems: [],
            isDefaultProfile: false,

            selectedFilters: [],
            allFilters: [],
            fieldMapping: {
                Offer: 'offer_id',
                Program: 'program_id',
                Campaign: 'campaign_id',
                Sub_campaign: 'sub_campaign',
                Geo: 'geo',
                Hour: 'hour',
                Affiliate: 'affiliate_id',
                Device: 'device',
                Verticals: 'vertical_name',
                Advertiser: 'advertiser_id',
                Affiliate_manager: 'affiliate_manager_id',
                Advertiser_manager: 'advertiser_manager_id',
            },

            availableOperators: [],

            compareTo: false,

            rangeFirstMenuItems: ['All Time','Current', 'Between', 'Previous', 'Last...'],
            rangeSecondMenuItems: [],
            firstMenuValue: 'All Time',
            secondMenuValue: null,
            currentItems: ['Today', 'This week', 'This month', 'This quarter', 'This year'],
            previousItems: ['Yesterday', 'Previous week', 'Previous month', 'Previous quarter', 'Previous year'],
            lastItems: ['Hour', 'Day', 'Week', 'Month', 'Quarter', 'Year'],
            selectedDateOne: null,
            selectedDateTwo: null,
            dateMenuOne: false,
            dateMenuTwo: false,
            lastValue: 0,
            compareFirstMenuValue: 'All Time',
            compareSecondMenuValue: null,
            compareSelectedDateOne: null,
            compareSelectedDateTwo: null,
            compareDateMenuOne: false,
            compareDateMenuTwo: false,
            compareLastValue: 0,
            range: {
                start: 0,
                end: 0
            },
            dates: [],
            compareRange: {
                start: 0,
                end: 0
            },
            compareDates: [],

        }),
        props: {
            isShow: Boolean,
        },
        async mounted() {
            this.dialog = this.isShow;
            this.profileSettings = [...this.getSetsOfSettings];

            this.selectedTabs = [...NAVIGATION_ITEMS];
            this.allTabs = [...NAVIGATION_ITEMS];

            this.selectedDrillDowns = [...DRILL_DOWNS];
            this.allDrillDowns = [...DRILL_DOWNS];

            this.selectedColumns = [...COLUMNS];
            this.allColumns = [...COLUMNS];

            this.allFilters = [...ALL_FILTERS];
            this.availableOperators = [...AVAILABLE_OPERATORS];
            this.saveOriginalData();
            this.updateSortableItems();
        },
        watch: {
            isShow() {
                this.dialog = !this.dialog;
            },
            getSetsOfSettings() {
                this.profileSettings = [...this.getSetsOfSettings];
            },
            selectedDrillDowns: {
                handler() {
                    const newDrillDowns = [...this.selectedDrillDowns]; // Текущие значения
                    const oldDrillDowns = [...this.previousDrillDowns || []]; // Предыдущее состояние (инициализируется при первом вызове)

                    console.log(`[WATCH_SELECTED_OKD] ${JSON.stringify(oldDrillDowns)}`);
                    console.log(`[WATCH_SELECTED_DRILLDOWNS] ${JSON.stringify(newDrillDowns)}`);
                    console.log(`[WATCH_SELECTED_COLUMNS] ${JSON.stringify(this.selectedColumns)}`);

                    const removedDrillDowns = oldDrillDowns.filter(item => !newDrillDowns.includes(item));

                    removedDrillDowns.forEach(role => {
                        const index = this.selectedColumns.indexOf(role);
                        if (index !== -1) {
                            this.selectedColumns.splice(index, 1);
                        }
                    });

                    this.previousDrillDowns = [...this.selectedDrillDowns];

                    console.log(`[UPDATED_SELECTED_COLUMNS] ${JSON.stringify(this.selectedColumns)}`);
                    this.updateSortableItems();
                },
                deep: true
            },
            selectedColumns: {
                handler() {
                    this.updateSortableItems();
                },
                deep: true
            },
            sortableItems: {
                handler() {
                    this.updateAvailableColumns();
                },
                deep: true
            },
            getCurrentSettings() {
                this.wasManuallySorted = false;
                if (this.getCurrentSettings.name) {
                    this.isDefaultProfile = this.getCurrentSettings.name === 'Default Profile';
                }

                if (!this.getCurrentSettings.settings || !this.getCurrentSettings.id) {
                    this.selectedSetting = null;
                    this.selectedSettingId = 0;
                    return;
                }

                if (this.getCurrentSettings.settings.dateValues) {
                    const dateSettings = this.getCurrentSettings.settings.dateValues;

                    if (dateSettings.firstMenuValue) {
                        this.firstMenuValue = dateSettings.firstMenuValue;
                    }
                    if (dateSettings.secondMenuValue) {
                        this.secondMenuValue = dateSettings.secondMenuValue;
                    }
                    if (dateSettings.lastValue) {
                        this.lastValue = dateSettings.lastValue;
                    }

                    if (dateSettings.dateOne && dateSettings.dateTwo) {
                        this.selectedDateOne = dateSettings.dateOne;
                        this.selectedDateTwo = dateSettings.dateTwo;
                    }

                    this.compileDateRange();

                    if (this.dates.length === 2) {
                        store.dispatch('settings/setDatesStore', this.dates);
                    }

                }
                if (this.getCurrentSettings.settings.compareDateValues) {
                    const compareDateSettings = this.getCurrentSettings.settings.compareDateValues;

                    if (compareDateSettings.compareFirstMenuValue) {
                        this.compareFirstMenuValue = compareDateSettings.compareFirstMenuValue;
                    }
                    if (compareDateSettings.compareSecondMenuValue) {
                        this.compareSecondMenuValue = compareDateSettings.compareSecondMenuValue;
                    }
                    if (compareDateSettings.compareLastValue) {
                        this.compareLastValue = compareDateSettings.compareLastValue;
                    }

                    if (compareDateSettings.compareDateOne && compareDateSettings.compareDateTwo) {
                        this.compareSelectedDateOne = compareDateSettings.compareDateOne;
                        this.compareSelectedDateTwo = compareDateSettings.compareDateTwo;
                    }

                    this.compileDateRange(true);

                    if (this.compareDates.length === 2) {
                        store.dispatch('settings/setCompareDatesStore', this.compareDates);
                    }
                }
                if (this.getCurrentSettings && this.getCurrentSettings.settings) {
                    this.selectedSettingId = this.getCurrentSettings.id;
                    this.selectedSetting = this.getCurrentSettings.name;
                    if (this.getCurrentSettings.settings.tabs && this.getCurrentSettings.settings.tabs.length) {
                        this.selectedTabs = [...this.getCurrentSettings.settings.tabs];
                    }
                    if (this.getCurrentSettings.settings.drillDowns && this.getCurrentSettings.settings.drillDowns.length) {
                        this.selectedDrillDowns = [...this.getCurrentSettings.settings.drillDowns];
                    }
                    if (this.getCurrentSettings.settings.columns && this.getCurrentSettings.settings.columns.length) {
                        this.selectedColumns = [...this.getCurrentSettings.settings.columns];
                    }
                    if (this.getCurrentSettings.settings.filters && this.getCurrentSettings.settings.filters.length) {
                        this.selectedFilters = [...this.getCurrentSettings.settings.filters];
                    }

                    if (typeof this.getCurrentSettings.settings.isShowBlacklisted === 'boolean') {
                        this.isShowBlacklisted = this.getCurrentSettings.settings.isShowBlacklisted
                    }

                    if (typeof this.getCurrentSettings.settings.onlyAggregatedStats === 'boolean') {
                        this.onlyAggregatedStats = this.getCurrentSettings.settings.onlyAggregatedStats
                    }

                    if (this.getCurrentSettings.settings.mode) {
                        this.mode = this.getCurrentSettings.settings.mode
                    }
                }
                if (this.getCurrentSettings.settings.sorting) {
                    this.sorting = this.convertSortingToArray(this.getCurrentSettings.settings.sorting);
                } else {
                    this.sorting = [];
                }
                this.updateSortableItems();
                this.updateAvailableColumns();
            },
        },
        computed: {
            ...mapGetters('settings', ['getSetsOfSettings', 'getCurrentSettings']),
            ...mapState('user', ['user']),
        },
        methods:{
            ...mapActions('settings', ['startLoading']),
            saveOriginalData() {
                this.originData = JSON.stringify({
                    selectedTabs: this.selectedTabs,
                    selectedDrillDowns: this.selectedDrillDowns,
                    selectedColumns: this.selectedColumns,
                    selectedFilters: this.selectedFilters,
                    isShowBlacklisted: this.isShowBlacklisted,
                    onlyAggregatedStats: this.onlyAggregatedStats,
                    mode: this.mode,
                    dateValues: {
                        firstMenuValue: this.firstMenuValue,
                        secondMenuValue: this.secondMenuValue,
                        lastValue: this.lastValue,
                        selectedDateOne: this.selectedDateOne,
                        selectedDateTwo: this.selectedDateTwo,
                    },
                    compareDates: this.compareDates,
                    compareTo: this.compareTo,
                });
            },
            isDataChanged() {
                const currentData = JSON.stringify({
                    selectedTabs: this.selectedTabs,
                    selectedDrillDowns: this.selectedDrillDowns,
                    selectedColumns: this.selectedColumns,
                    selectedFilters: this.selectedFilters,
                    isShowBlacklisted: this.isShowBlacklisted,
                    onlyAggregatedStats: this.onlyAggregatedStats,
                    mode: this.mode,
                    dateValues: {
                        firstMenuValue: this.firstMenuValue,
                        secondMenuValue: this.secondMenuValue,
                        lastValue: this.lastValue,
                        selectedDateOne: this.selectedDateOne,
                        selectedDateTwo: this.selectedDateTwo,
                    },
                    compareDates: this.compareDates,
                    compareTo: this.compareTo,
                });

                return this.originData !== currentData;
            },
            handleSelection(value) {
                if (!value) return;
                const existingSetting = this.profileSettings.find((item) => item.name === value || item.name === value.name);
                if (existingSetting && existingSetting.id && existingSetting.settings.dateValues) {
                    console.log(`[SELECTED_PROFILE] ${JSON.stringify(existingSetting)}`);
                    this.firstMenuValue = existingSetting.settings.dateValues.firstMenuValue;
                    this.secondMenuValue = existingSetting.settings.dateValues.secondMenuValue || null;
                    this.lastValue = existingSetting.settings.dateValues.lastValue || 0;
                    this.selectedDateOne = existingSetting.settings.dateValues.dateOne || null;
                    this.selectedDateTwo = existingSetting.settings.dateValues.dateTwo || null;
                    this.selectedFilters = existingSetting.settings.filters;

                    if (existingSetting.settings.compareDates) {
                        this.compareFirstMenuValue = existingSetting.settings.compareDateValues.compareFirstMenuValue || 'All Time';
                        this.compareSecondMenuValue = existingSetting.settings.compareDateValues.compareSecondMenuValue || null;
                        this.compareLastValue = existingSetting.settings.compareDateValues.compareLastValue || 0;
                        this.compareSelectedDateOne = existingSetting.settings.compareDateValues.compareDateOne || null;
                        this.compareSelectedDateTwo = existingSetting.settings.compareDateValues.compareDateTwo || null;
                        this.compareTo = true;
                    } else {
                        this.compareFirstMenuValue = 'All Time';
                        this.compareSecondMenuValue = null;
                        this.compareLastValue = 0;
                        this.compareSelectedDateOne = null;
                        this.compareSelectedDateTwo = null;
                        this.compareTo = false;
                    }
                    store.dispatch('settings/setCurrentSettingsStore', existingSetting.id);
                    this.selectedSettingId = existingSetting.id;
                    this.selectedSetting = existingSetting.name;
                } else {
                    this.selectedSetting = typeof value === 'string' ? value : value.name;
                }
            },
            isNewSettings(name) {
              return !this.profileSettings.some((item) => item.name === name);
            },
            handleBlur(event) {
                const value = event.target.value;
                if (value) {
                    this.selectedSetting = value;
                }
            },
            handleInput(value) {
               if (value === null) {
                   this.isDefaultProfile = false;
               }
            },
            addTab() {
                if (this.selectedTabs.length < this.allTabs.length) {
                    this.selectedTabs.push("");
                }
            },
            addDrillDown() {
                if (this.selectedDrillDowns.length < this.allDrillDowns.length) {
                    this.selectedDrillDowns.push("");
                }
            },
            addColumn() {
                if (this.sortableItems.length < this.allColumns.length) {
                    const newCol = { name: "", isDrillDown: false };
                    this.sortableItems.push(newCol);
                    this.updateAvailableColumns();
                }
            },
            removeTab(index) {
                this.selectedTabs.splice(index, 1);
            },
            removeDrillDown(index) {
                console.log(`index ${index}`);
                console.log(`this.selectedDrillDowns before ${JSON.stringify(this.selectedDrillDowns)}`);

                const removedDrillDown = this.selectedDrillDowns[index];

                this.selectedDrillDowns.splice(index, 1);
                this.$delete(this.sorting, removedDrillDown);

                if (this.selectedDrillDowns.length === 0) {
                    this.selectedDrillDowns.push("Offer");
                }

                console.log(`this.selectedDrillDowns after ${JSON.stringify(this.selectedDrillDowns)}`);
            },

            removeColumn(index) {
                const itemToRemove = this.sortableItems[index];

                if (!itemToRemove || itemToRemove.isDrillDown) {
                    return;
                }

                this.sortableItems.splice(index, 1);

                this.selectedColumns = this.selectedColumns.filter(col => col !== itemToRemove.name);

                this.sorting = this.sorting.filter(sort => sort.field !== itemToRemove.name);


                this.updateAvailableColumns();
            },

            handleNameChange(newName, index) {
                const item = this.sortableItems[index];
                if (!item || item.isDrillDown) return;

                const oldName = item.name;

                this.sortableItems[index].name = newName;

                const oldIndex = this.selectedColumns.indexOf(oldName);
                if (oldIndex !== -1) {
                    this.selectedColumns.splice(oldIndex, 1);
                }

                if (!this.selectedColumns.includes(newName)) {
                    this.selectedColumns.push(newName);
                }

                this.updateAvailableColumns();
            },

            updateAvailableColumns() {
                const selectedNames = this.sortableItems.map(i => i.name);
                this.avaliableColumns = this.allColumns.filter(col => !selectedNames.includes(col));
            },
            filteredDrillDowns(index) {
                return this.allDrillDowns.filter(
                    (item) => !this.selectedDrillDowns.includes(item) || this.selectedDrillDowns[index] === item
                );
            },
            updateMainRange(range) {
                Object.assign(this, range);
                this.compileDateRange();
            },
            updateCompareRange(range) {
                Object.assign(this, {
                    compareFirstMenuValue: range.firstMenuValue,
                    compareSecondMenuValue: range.secondMenuValue,
                    compareLastValue: range.lastValue,
                    compareSelectedDateOne: range.selectedDateOne,
                    compareSelectedDateTwo: range.selectedDateTwo,
                });

                this.compileDateRange();
            },
            async applySettings() {
              await this.startLoading();
              store.dispatch('settings/setAppliedSettingsStore', this.selectedSettingId);
              store.dispatch('settings/updateAppliedSettingsStore', this.compileSettingsData());
                if (this.dates.length === 2) {
                    store.dispatch('settings/setDatesStore', this.dates);
                }
                if (this.compareDates.length === 2 && this.compareTo) {
                    store.dispatch('settings/setCompareDatesStore', this.compareDates);
                } else {
                    store.dispatch('settings/setCompareDatesStore', null);
                }
                this.updateSortableItems();
                this.close();

            },
            addFilter() {
                const lastId = this.selectedFilters.length ? this.selectedFilters[this.selectedFilters.length - 1].id : 0;
                this.selectedFilters.push({
                    id: lastId+1,
                    column: '',
                    field:'',
                    operator: '',
                    value: '',
                });
            },
            updateField(filter) {
              // console.log(`filter 1  ${JSON.stringify(filter)}`)
              // console.log(`filter this.fieldMapping  ${JSON.stringify(this.fieldMapping)}`)
              filter.field = this.fieldMapping[filter.column] || '';
              // console.log(`filter 2 ${JSON.stringify(filter)}`)
            },
            removeFilter(id) {
                this.selectedFilters = this.selectedFilters.filter(filter => filter.id !== id);
            },
            resetSettings() {
                this.selectedTabs = [...NAVIGATION_ITEMS];
                this.selectedDrillDowns = [...DRILL_DOWNS];
                this.selectedColumns = [...COLUMNS];
                this.selectedFilters = [];
                store.dispatch('settings/resetSettingsStore', this.selectedSettingId);
            },
            compileDateRange(isCompare = false) {
                const getRange = (menuValue, secondMenuValue, selectedDateOne, selectedDateTwo, lastValue) => {
                    switch (menuValue) {
                        case "All Time":
                            return { start: 0, end: 0 };
                        case "Current":
                            if (secondMenuValue === "Today") return { ...dateRange.getToday() };
                            if (secondMenuValue === "This week") return { ...dateRange.getThisWeek() };
                            if (secondMenuValue === "This month") return { ...dateRange.getThisMonth() };
                            if (secondMenuValue === "This quarter") return { ...dateRange.getThisQuarter() };
                            if (secondMenuValue === "This year") return { ...dateRange.getThisYear() };
                            break;
                        case "Between":
                            if (selectedDateOne && selectedDateTwo) {
                                const d1 = new Date(selectedDateOne).getTime();
                                const d2 = new Date(selectedDateTwo).getTime();
                                if (d1 <= d2) return { start: d1, end: d2 };
                            }
                            break;
                        case "Previous":
                            if (secondMenuValue === "Yesterday") return { ...dateRange.getYesterday() };
                            if (secondMenuValue === "Previous week") return { ...dateRange.getPreviousWeek() };
                            if (secondMenuValue === "Previous month") return { ...dateRange.getPreviousMonth() };
                            if (secondMenuValue === "Previous quarter") return { ...dateRange.getPreviousQuarter() };
                            if (secondMenuValue === "Previous year") return { ...dateRange.getPreviousYear() };
                            break;
                        case "Last...":
                            if (secondMenuValue && lastValue) {
                                return { ...dateRange.getLast(Number(lastValue), secondMenuValue.toLowerCase()) };
                            }
                            break;
                    }
                    return { start: 0, end: 0 };
                };

                this.range = getRange(this.firstMenuValue, this.secondMenuValue, this.selectedDateOne, this.selectedDateTwo, this.lastValue);
                this.dates = this.range.start && this.range.end
                    ? [Math.round(this.range.start / 1000), Math.round(this.range.end / 1000)]
                    : [];

                this.compareRange = getRange(this.compareFirstMenuValue, this.compareSecondMenuValue, this.compareSelectedDateOne, this.compareSelectedDateTwo, this.compareLastValue);
                this.compareDates = this.compareRange.start && this.compareRange.end
                    ? [Math.round(this.compareRange.start / 1000), Math.round(this.compareRange.end / 1000)]
                    : [];
                if (isCompare){
                    this.compareTo = this.compareDates.length === 2;
                }
                if (this.dates.length === 2) {
                    const fromDateHuman = new Date(this.dates[0] * 1000).toISOString().replace('T', ' ').split('.')[0];
                    const toDateHuman = new Date(this.dates[1] * 1000).toISOString().replace('T', ' ').split('.')[0];
                    console.log(`[COMPLETE_DATA_RANGE] ${fromDateHuman} - ${toDateHuman}`)
                }
                if (this.compareDates.length === 2) {
                    const fromDateCompareHuman = new Date(this.compareDates[0] * 1000).toISOString().replace('T', ' ').split('.')[0];
                    const toDateCompareHuman = new Date(this.compareDates[1] * 1000).toISOString().replace('T', ' ').split('.')[0];
                    console.log(`[COMPLETE_DATA_RANGE_COMPARE] ${fromDateCompareHuman} - ${toDateCompareHuman}`)
                }

            },
            compileSettingsData() {
                const dates = this.range.start && this.range.end ? [this.range.start, this.range.end] : [];
                const compareDates = this.compareRange.start && this.compareRange.end
                    ? [this.compareRange.start, this.compareRange.end]
                    : [];

                const newSelectedColumns = this.sortableItems.filter(i => !i.isDrillDown).map(i => i.name);

                const sorting = this.sortableItems.map(item => {
                    const existingSort = this.sorting.find(sort => sort.field === item.name);
                    return existingSort ? existingSort : { field: item.name, order: "asc" };
                });

                return {
                    tabs: this.selectedTabs,
                    drillDowns: this.selectedDrillDowns,
                    columns: newSelectedColumns,
                    sorting: sorting,
                    isShowBlacklisted: this.isShowBlacklisted,
                    onlyAggregatedStats: this.onlyAggregatedStats,
                    mode: this.mode,
                    dates,
                    compareDates: this.compareTo ? compareDates : null,
                    filters: this.selectedFilters,
                    dateValues: {
                        dates,
                        firstMenuValue: this.firstMenuValue,
                        secondMenuValue: this.secondMenuValue,
                        lastValue: this.lastValue,
                        dateOne: this.selectedDateOne,
                        dateTwo: this.selectedDateTwo,
                    },
                    ...(this.compareTo && {
                        compareDateValues: {
                            compareDates,
                            compareFirstMenuValue: this.compareFirstMenuValue,
                            compareSecondMenuValue: this.compareSecondMenuValue,
                            compareLastValue: this.compareLastValue,
                            compareDateOne: this.compareSelectedDateOne,
                            compareDateTwo: this.compareSelectedDateTwo,
                        }
                    })
                };
            },
            async saveSettings() {
                if (!this.selectedSetting || this.selectedSetting.trim() === '') {
                    alert('Please enter a name for the settings');
                    return
                }
                if (this.isNewSettings(this.selectedSetting)) {
                    const res = await store.dispatch('settings/saveSettingsStore', {
                        name: this.selectedSetting,
                        settings: this.compileSettingsData()
                    });
                    if (res){
                        this.saveOriginalData();
                        await this.$dialog.notify.success(`new profile saved successfully`, {
                            position: 'top-right',
                            timeout: 2000
                        })
                    }

                } else {
                    const res = await store.dispatch('settings/updateSettingsStore', {
                        name: this.selectedSetting,
                        settings: this.compileSettingsData()
                    });

                    if (res){
                        this.saveOriginalData();
                        await this.$dialog.notify.success(`profile updated successfully`, {
                            position: 'top-right',
                            timeout: 2000
                        })
                    }

                }
            },
            handleSortingChange(value, field) {
                if (!Array.isArray(this.sorting)) {
                    this.sorting = [];
                }

                const existingIndex = this.sorting.findIndex(item => item.field === field);

                if (existingIndex !== -1) {
                    this.sorting[existingIndex].order = value;
                } else {
                    this.sorting.push({ field, order: value });
                }
            },
            getSortingOrder(field) {
                const entry = this.sorting.find(item => item.field === field);
                return entry ? entry.order : null;
            },

            updateSortableItems() {
                if (this.wasManuallySorted) {
                    return;
                }

                const sortedItems = [];

                this.sorting.forEach(sort => {
                    if (this.selectedDrillDowns.includes(sort.field)) {
                        sortedItems.push({ name: sort.field, isDrillDown: true });
                    } else if (this.selectedColumns.includes(sort.field)) {
                        sortedItems.push({ name: sort.field, isDrillDown: false });
                    }
                });

                this.selectedDrillDowns.forEach(drillDown => {
                    if (!sortedItems.some(item => item.name === drillDown)) {
                        sortedItems.unshift({ name: drillDown, isDrillDown: true });
                    }
                });

                this.selectedColumns.forEach(column => {
                    if (!sortedItems.some(item => item.name === column)) {
                        sortedItems.push({ name: column, isDrillDown: false });
                    }
                });

                this.sortableItems = sortedItems;
            },
            isSortedField(field) {
                if (!Array.isArray(this.sorting)) return false;
                return this.sorting.some(item => item.field === field);
            },
            onDragStart(index, event) {
                event.dataTransfer.setData("index", index);
            },
            onDrop(index, event) {
                const oldIndex = Number(event.dataTransfer.getData("index"));
                if (oldIndex === index) return;

                const movedItem = this.sortableItems.splice(oldIndex, 1)[0];
                this.sortableItems.splice(index, 0, movedItem);

                this.wasManuallySorted = true;
                this.updateAvailableColumns();
            },
            convertSortingToArray(sortingObject) {
                if (Array.isArray(sortingObject)) {
                    return sortingObject;
                }
                return Object.entries(sortingObject).map(([field, order]) => ({
                    field,
                    order
                }));
            },
            close() {
                this.dialog = false;
                this.items = [];
            },
        }
    }
</script>

<style lang="sass">
    .headSection
        padding-top: 1%
        display: flex
        justify-content: space-between
        align-items: center
        .centered-child
            margin-left: 40%
            font-weight: bold
            font-size: 12pt
            color: #3a3852

        .right-aligned-child
            font-weight: bold
            margin-left: auto
            font-size: 16pt
            margin-right: 3%
            &:hover
                cursor: pointer
    .title-settings
        width: 100%
    .custom-combobox
        width: 100% !important
        margin-right: 2% !important
        min-width: 240px !important
    .label-misc
        min-width: 100px !important

    .disabled-select
        .v-input__control
            div.v-input__slot
                background-color: #f0f0f0 !important
                .v-select__slot
                    .v-select__selections
                        .v-select__selection--comma
                            color: black !important

    .active-sort
        .v-input__control
            div.v-input__slot
                background-color: white !important

</style>


